import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Phone, ArrowRight, ChatCircleText, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api/customer/auth';

// iOS Safari needs "&body=" instead of "?body=" in sms: links; everything else
// (Android, desktop) accepts "?body=".
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

export default function CustomerPhoneSetup() {
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('enter'); // 'enter' | 'verify' | 'verified'
  const [verifyInfo, setVerifyInfo] = useState(null); // { code, receiving_number }
  const [polling, setPolling] = useState(false);
  const pollRef = useRef(null);

  // Silently capture location in background — no UI shown
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const token = localStorage.getItem('customer_token');
          if (token) {
            fetch(`${API_BASE}/location`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
            }).catch(() => {});
          }
        },
        () => {},
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }
  }, []);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleRequestVerification = async () => {
    if (phone.length < 10) {
      toast.error('Enter a valid 10-digit phone number');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('customer_token');
      const res = await fetch(`${API_BASE}/phone/request-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ phone }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not start verification');
      }
      const data = await res.json();
      setVerifyInfo(data);
      setStep('verify');
      startPolling();
    } catch (err) {
      toast.error(err.message || 'Failed to start verification');
    } finally {
      setLoading(false);
    }
  };

  const checkVerificationOnce = async () => {
    try {
      const res = await fetch(`${API_BASE}/phone/verification-status?phone=${phone}`);
      const data = await res.json();
      if (data.verified) {
        if (pollRef.current) clearInterval(pollRef.current);
        setPolling(false);
        await finalizePhoneSave();
        return true;
      } else if (data.expired) {
        if (pollRef.current) clearInterval(pollRef.current);
        setPolling(false);
        toast.error('Verification code expired — please try again');
        setStep('enter');
        return true;
      }
    } catch {
      // transient network hiccup — keep polling
    }
    return false;
  };

  const startPolling = () => {
    setPolling(true);
    pollRef.current = setInterval(checkVerificationOnce, 3000);
  };

  // Mobile browsers routinely pause a backgrounded tab's timers while the
  // customer is in their Messages app sending the SMS. This catches the
  // exact moment they switch back and checks immediately, instead of the
  // screen sitting stuck until the throttled interval happens to resume.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && step === 'verify') {
        checkVerificationOnce();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
    // eslint-disable-next-line
  }, [step, phone]);

  const finalizePhoneSave = async () => {
    try {
      const token = localStorage.getItem('customer_token');
      const res = await fetch(`${API_BASE}/phone`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ phone }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save phone');
      }
      const user = JSON.parse(localStorage.getItem('customer_user') || '{}');
      user.phone = phone;
      user.needs_phone = false;
      localStorage.setItem('customer_user', JSON.stringify(user));
      setStep('verified');
      toast.success('Phone verified!');
      setTimeout(() => { window.location.href = '/order'; }, 1200);
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    }
  };

  const openSmsApp = () => {
    if (!verifyInfo) return;
    const body = `SSPICE ${verifyInfo.code}`;
    const separator = isIOS() ? '&' : '?';
    const link = `sms:${verifyInfo.receiving_number}${separator}body=${encodeURIComponent(body)}`;
    window.location.href = link;
  };

  if (step === 'verify') {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6" data-testid="phone-verify-page">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm text-center">
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
            <ChatCircleText size={28} weight="duotone" className="text-sspice" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">One tap to verify</h2>
          <p className="text-sm text-gray-500 mb-6">
            We've pre-filled a text message for you — just hit Send in your messaging app, nothing to type.
          </p>

          <button
            data-testid="open-sms-app-btn"
            onClick={openSmsApp}
            className="w-full py-3.5 bg-sspice hover:bg-sspice-hover text-white font-semibold rounded-full flex items-center justify-center gap-2 transition-all mb-4"
          >
            <ChatCircleText size={18} weight="bold" />
            Open Messages & Send
          </button>

          <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
            {polling && (
              <>
                <span className="w-2 h-2 rounded-full bg-sspice animate-pulse" />
                Waiting for your message…
              </>
            )}
          </div>

          <button
            data-testid="back-to-phone-entry-btn"
            onClick={() => { if (pollRef.current) clearInterval(pollRef.current); setPolling(false); setStep('enter'); }}
            className="w-full mt-6 py-2 text-sm text-gray-400 hover:text-sspice transition-colors"
          >
            Wrong number? Go back
          </button>
        </motion.div>
      </div>
    );
  }

  if (step === 'verified') {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
          <CheckCircle size={56} weight="duotone" className="text-green-500 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-gray-900">Verified!</h2>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6" data-testid="phone-setup-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
          <Phone size={28} weight="duotone" className="text-sspice" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 text-center mb-1">Contact Number</h2>
        <p className="text-sm text-gray-500 text-center mb-6">
          We'll use this to coordinate your delivery
        </p>

        <div className="relative mb-2">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-2 text-gray-400">
            <span className="text-sm font-medium">+91</span>
            <span className="text-gray-200">|</span>
          </div>
          <input
            data-testid="phone-setup-input"
            type="tel"
            maxLength={10}
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => e.key === 'Enter' && handleRequestVerification()}
            placeholder="Enter phone number"
            className="w-full pl-20 pr-4 py-4 bg-white border border-gray-200 rounded-xl text-gray-900 text-lg focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-sspice transition-all"
          />
        </div>
        <p className="text-[11px] text-gray-400 mb-5 px-1">
          We verify this number is really yours via a one-tap text message — no OTP to type.
        </p>

        <button
          data-testid="save-phone-btn"
          onClick={handleRequestVerification}
          disabled={loading || phone.length < 10}
          className="w-full py-3.5 bg-sspice hover:bg-sspice-hover disabled:opacity-40 text-white font-semibold rounded-full flex items-center justify-center gap-2 transition-all"
        >
          {loading ? 'Please wait...' : 'Continue'}
          {!loading && <ArrowRight size={18} weight="bold" />}
        </button>
      </motion.div>
    </div>
  );
}
